import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { auth, profileAPI, sms, kycAPI } from 'services';
import useUniversalStore from 'store/universalStore';
import { images } from 'assets/themes';
import { toast } from 'sonner';

import { Navigate, useNavigate } from 'react-router-dom';
import { validatePhone } from 'utils';
import { MyPhoneInput } from 'components';
import { identifyUser, trackEvent } from 'lib/analytics';

const Login = () => {
    const { t } = useTranslation();
    const { user, write } = useUniversalStore((state) => state);
    const navigate = useNavigate();
    const RESEND_WAIT_TIME = 30;
    const ERROR_TECHNICAL_MESSAGE = "Technical error. Please try again later.";
    const [otpSent, setOtpSent] = useState(false)
    const [invalid, setInValid] = useState(true)
    const [otpVerified, setOtpVerified] = useState(false)
    const [resendWaitTime, setResendWaitTime] = useState(RESEND_WAIT_TIME)
    const [loading, setLoading] = useState(false)
    const [values, setValues] = useState({
        phone: '',
        otp: '',
    })

    const notify = (type, message) => {
        if (type == "error") {
            toast.error(message);
        } else {
            toast.success(message);
        }
    }

    useEffect(() => {
        if (resendWaitTime && otpSent && !otpVerified)
            setTimeout(() => {
                setResendWaitTime(() => resendWaitTime - 1)
                // console.log(resendWaitTime);
            }, 1000)
    }, [resendWaitTime])

    useEffect(() => {
        const isValid = validatePhone(values.phone);
        setInValid(isValid !== true);
    }, [values.phone])

    const handlePhoneChange = (phone) => {
        setValues((values) => ({
            ...values,
            phone: phone
        }))
    }

    const handleOtpChange = (e) => {
        setValues((values) => ({
            ...values,
            otp: e.target.value
        }))
    }

    const sendOTP = async (e) => {
        e.preventDefault();
        setLoading(true);
        setOtpVerified(false);

        try {
            const formattedPhone = values.phone.replace('+', '');
            const result = await sms.sendOTP(formattedPhone);
            if (result?.type == 'success') {
                // otp sent
                setResendWaitTime(29)
                setOtpSent(true);
                notify("success", 'OTP sent to ' + values.phone + ' successfully !');
            } else if (result.message === "XMLHttpRequest failed: \"Unable to connect to the Parse API\"") {
                // error due to App being Offline or Backend being down
                notify("error", ERROR_TECHNICAL_MESSAGE);
            } else {
                // Handle all errors like "OTP retry count maxed out" etc.
                notify("error", result.message)
            }
            setLoading(false);
        } catch (error) {
            setLoading(false);
            // Handles any crashes or unexpected errors
            notify("error", error.message);
        }
    }

    const resendOTP = async (e, resendType) => {
        e.preventDefault();
        setLoading(true);
        setOtpVerified(false);
        // alert("resend")
        try {
            const formattedPhone = values.phone.replace('+', '');
            const result = await sms.resendOTP(formattedPhone, resendType);
            if (result.type == 'success') {
                if (resendType === "text") {
                    notify("success", 'OTP resent successfully !')
                } else {
                    notify("success", 'Call requested for OTP !')
                }
                setResendWaitTime(29)
            } else if (result.message === "XMLHttpRequest failed: \"Unable to connect to the Parse API\"") {
                // error due to App being Offline or Backend being down
                notify("error", ERROR_TECHNICAL_MESSAGE);
            } else {
                // Handle all errors like "OTP retry count maxed out" etc.
                notify("error", result.message)
            }
            setLoading(false);
        } catch (error) {
            setLoading(false);
            // Handles any crashes or unexpected errors
            notify("error", error.message);
        }
    }

    const verifyOTP = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const formattedPhone = values.phone.replace('+', '');
            const result = await sms.verifyOTP(
                formattedPhone,
                values.otp
            );
            const { type, sessionToken } = result;

            if (!sessionToken) {
                notify("error", "Invalid OTP. Please try again.");
                setLoading(false)
                return;
            }

            // 2) Post login flow
            await handlePostLoginFlow(sessionToken, type);
        } catch (error) {
            // Handles any crashes or unexpected errors
            setLoading(false);
            notify("error", error.message);
        }
    }

    const handlePostLoginFlow = async (sessionToken, type) => {
        try {
            //  Login user with session token
            const result = await auth.loginUsingSession(sessionToken);
            const { status, response: user } = result;
            if (status !== "success") {
                notify("error", "Login failed. Please try again.");
                setLoading(false);
                return;
            }

            //get basic data
            const basic = await profileAPI.getProfileBasics(user);

            const email = basic?.contact?.email;
            const businessName = basic?.business?.name;
            const typeOfBusiness = basic?.business?.type;

            if (type === "signup") {
                identifyUser(user.id, { $first_name: user.get("username") });
                trackEvent("Signup")

                navigate("/signup", { replace: true })
                return;
            }

            if (type === "login" && (!businessName || !typeOfBusiness)) {
                identifyUser(user.id, {
                    $first_name: businessName,
                    $email: email,
                    phone: user.get("username")
                });
                trackEvent("Login")

                navigate("/signup", { replace: true })
                return;
            }

            write({ user: user, userBasic: basic })
            identifyUser(user.id, {
                $first_name: businessName,
                $email: email,
                phone: user.get("username")
            });
            navigate("/", { replace: true });

        } catch (error) {
            console.error("handlePostLoginFlow error:", error);
            notify("error", error.message);
        } finally {
            setLoading(false)
        }
    };


    if (user) return <Navigate to="/" />

    return (
        <div className="auth-container">
            <div className="auth-wrapper">
                <div className="auth-card">
                    <div className="auth-header">
                        <img src={images.logo} alt="Logo" className="auth-logo" />
                        <div className="auth-title">
                            <h1>Welcome to</h1>
                            <h1 className="text-danger">{t('Bahi Khata')}</h1>
                        </div>
                    </div>

                    <form onSubmit={otpSent ? verifyOTP : sendOTP} className="auth-form">
                        <div className="phone-input-wrapper d-flex align-items-center gap-2">
                            <MyPhoneInput
                                field={{
                                    value: values.phone,
                                    onChange: handlePhoneChange,
                                }}
                                error={invalid}
                                isSubmitting={otpSent}
                            />
                            {otpSent && <i className="bi bi-pencil" onClick={() => setOtpSent(false)}></i>}
                        </div>

                        {otpSent && (
                            <div className="otp-section">
                                <input
                                    type="number"
                                    className="form-control border py-auto"
                                    placeholder="Enter OTP"
                                    maxLength={4}
                                    onInput={(e) => {
                                        e.target.value = e.target.value.replace(/[^0-9]/g, '').slice(0, 4)
                                    }}
                                    value={values.otp}
                                    onChange={handleOtpChange}
                                    required
                                />

                                <div className="resend-section">
                                    {resendWaitTime > 0 ? (
                                        <span className="timer">Resend OTP in {resendWaitTime}s</span>
                                    ) : (
                                        <div className="resend-buttons">
                                            <button onClick={(e) => resendOTP(e, "text")} className="btn btn-outline-danger">
                                                Resend via SMS
                                            </button>
                                            <button onClick={(e) => resendOTP(e, "call")} className="btn btn-outline-danger">
                                                Get OTP on Call
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        <button
                            type="submit"
                            disabled={invalid || !values.phone || loading}
                            className="btn btn-danger py-3"
                        >
                            {loading ? (
                                <span className="spinner-border spinner-border-sm me-2"></span>
                            ) : null}
                            {loading ? 'Please wait...' : otpSent ? 'Verify OTP' : 'Send OTP'}
                        </button>
                    </form>
                </div>
            </div>
        </div >
    )
}

export default Login
